import { useEffect, useState } from "react";
import { Volume2, VolumeX, X } from "lucide-react";
import { FlowControls, LanguagePill } from "@/components/session/FlowControls";
import { Presence } from "@/components/Presence";
import { Notes, Spool } from "@/components/session/Notes";
import { useIsDesktop } from "@/hooks/useIsDesktop";

// the form takes whatever room is left once the header, the line and the controls have theirs
const useStageHeight = () => {
  const [h, setH] = useState(() => Math.round(Math.min(430, Math.max(200, window.innerHeight * 0.4))));
  useEffect(() => {
    const on = () => setH(Math.round(Math.min(430, Math.max(200, window.innerHeight * 0.4))));
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("orientationchange", on); };
  }, []);
  return h;
};

const LINE = {
  idle: "listening",
  listening: "listening",
  reflecting: "·· ·",
  speaking: "speaking · tap to cut in",
};

// Flowing: the form carries the state. Two controls — mute, and out.
export const FlowView = ({ state, accent, labName, turns, muted, toggleMute, leaveFlow, end, showTranscript, showGreeting, greeting, notes = [], kept = new Set(), onKeep, noteMotion = true, volume = 1, onVolume, output = "speaker", onOutput, ios = false, awayClosed = false, awayCopy = "", onResumeAway, allowText = true, toText, needsTap = false, onWake, lang = "auto", onLang, onInterrupt }) => {
  const stageH = useStageHeight();
  const isDesktop = useIsDesktop();
  // on a phone, once there are words they take the page and he steps aside into a small bar
  const spool = !isDesktop && notes.length > 0;
  const formH = stageH;
  const [sound, setSound] = useState(false);
  const lastGriot = [...turns].reverse().find((t) => t.role === "griot");
  const lastUser = [...turns].reverse().find((t) => t.role === "user");
  const label = muted ? "muted · I can't hear the room" : LINE[state] || LINE.idle;
  const hint = state === "speaking" || awayClosed ? "" : "tap to see the thought";

  return (
    <div
      className="grain lamp relative flex flex-col overflow-hidden"
      style={{ height: "100dvh", "--lab-accent": accent, background: "var(--bg-primary)" }}
      data-testid="flow-view"
    >
      <header className="relative z-10 flex shrink-0 items-center justify-between px-6 lg:px-14" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 1.1rem)" }}>
        <div className="flex min-w-0 items-center gap-3">
          <span className="h-8 w-[2px] shrink-0 rounded-full" style={{ background: `linear-gradient(180deg, transparent, ${accent}, transparent)` }} />
          <p className="eyebrow truncate">{labName}</p>
        </div>
        <div className="flex items-center gap-2.5">
          {spool && <LanguagePill lang={lang} onLang={onLang} accent={accent} />}
          <button
            data-testid="flow-leave-btn"
            onClick={end}
            aria-label="Leave the conversation"
            className="flex h-10 w-10 items-center justify-center rounded-full border transition-colors duration-300 hover:text-[#F4EFE6]"
            style={{ borderColor: "var(--border-linen)", color: "var(--text-faint)" }}
          >
            <X size={16} />
          </button>
        </div>
      </header>

      <div className={`absolute right-3 top-1/2 z-20 -translate-y-1/2 lg:right-8 ${spool ? "hidden lg:block" : ""}`}>
        <LanguagePill lang={lang} onLang={onLang} accent={accent} vertical />
      </div>

      {spool ? (
        <>
          {/* he steps aside into a small bar so the words can have the page */}
          <button
            data-testid="flow-stage"
            onClick={onInterrupt}
            aria-label={state === "speaking" ? "Cut in" : "Show me that"}
            className="relative z-10 flex shrink-0 items-center gap-3.5 px-6 pb-1 pt-2 text-left"
          >
            <span className="shrink-0 overflow-hidden" style={{ width: 92, height: 58 }}>
              <Presence state={muted && state === "idle" ? "idle" : state} accent={accent} height={58} />
            </span>
            <span className="font-mono text-[9px] uppercase leading-[1.7] tracking-[0.2em]" data-testid="flow-state-title" style={{ color: muted ? accent : "var(--text-faint)" }}>
              {label}
              {hint && <><br /><span className="opacity-50">{hint}</span></>}
            </span>
          </button>
          <Spool notes={notes} accent={accent} kept={kept} onKeep={onKeep} />
        </>
      ) : (
      <button
        data-testid="flow-stage"
        onClick={onInterrupt}
        aria-label={state === "speaking" ? "Cut in" : "Show me that"}
        className="relative z-10 flex min-h-0 flex-1 cursor-default flex-col items-center justify-start overflow-y-auto overflow-x-hidden px-6 py-2 text-center"
      >
        {/* m-auto, not justify-center: a centred flex child gets clipped at BOTH ends once the
            content outgrows the box, which is how notes went missing on a phone */}
        <div className="m-auto flex w-full flex-col items-center">
        <div className="w-full max-w-[640px] shrink-0">
          <Presence state={muted && state === "idle" ? "idle" : state} accent={accent} height={formH} />
        </div>

        <p
          className="mt-4 font-mono text-[10px] uppercase tracking-[0.26em] transition-colors duration-500"
          data-testid="flow-state-title"
          style={{ color: muted ? accent : "var(--text-faint)" }}
        >
          {label}
        </p>
        {hint && (
          <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.22em] opacity-45" data-testid="flow-pull-hint" style={{ color: "var(--text-faint)" }}>
            {hint}
          </p>
        )}

        {showGreeting && greeting && turns.length === 0 && (
          <p className="ember-in mt-5 max-w-[34rem] font-display text-[17px] font-light italic leading-[1.55] lg:mt-7 lg:text-[21px]" data-testid="flow-greeting" style={{ color: "var(--text-dim)" }}>
            “{greeting}”
          </p>
        )}

        {notes.length > 0 && <Notes notes={notes} accent={accent} motion={noteMotion} />}

        {showTranscript && lastGriot && (
          <div className="ember-in mt-5 max-w-[34rem] space-y-3 lg:mt-7" data-testid="flow-transcript">
            {lastUser && <p className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--text-faint)" }}>you · {lastUser.text}</p>}
            <p className="font-display text-[18px] font-light leading-[1.5] lg:text-[21px]" data-testid="griot-response" style={{ color: "var(--text-main)" }}>{lastGriot.text}</p>
          </div>
        )}
        </div>
      </button>
      )}

      <div
        className="relative z-10 flex shrink-0 flex-col items-center gap-3 px-6 pt-2"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.4rem)" }}
      >
        {needsTap && (
          <button
            data-testid="flow-wake-btn"
            onClick={onWake}
            className="ember-in mb-1 rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em]"
            style={{ borderColor: accent, color: accent, background: `${accent}14` }}
          >
            tap to hear him
          </button>
        )}

        {awayClosed && (
          <div className="ember-in mb-1 flex flex-col items-center gap-2" data-testid="flow-away-notice">
            <p className="max-w-[20rem] font-display text-[15px] font-light italic leading-[1.5]" style={{ color: "var(--text-dim)" }}>
              {awayCopy || "I let the line go quiet while you were away. Mimi bado hapa — I am still here."}
            </p>
            <button
              data-testid="flow-resume-btn"
              onClick={onResumeAway}
              className="rounded-full border px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
              style={{ borderColor: `${accent}88`, color: accent }}
            >
              sit back down
            </button>
          </div>
        )}

        {sound && (
          <div
            className="ember-in mb-1 flex w-full max-w-[20rem] flex-col gap-3 rounded-2xl border px-4 py-3.5"
            data-testid="flow-sound-panel"
            style={{ borderColor: "var(--border-linen)", background: "rgba(244,239,230,0.03)" }}
          >
            <div className="flex items-center gap-3">
              <VolumeX size={13} style={{ color: "var(--text-faint)" }} />
              <input
                data-testid="flow-volume-slider"
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={(e) => onVolume?.(Number(e.target.value))}
                className="flow-volume h-1 flex-1 cursor-pointer appearance-none rounded-full"
                style={{ background: `linear-gradient(90deg, ${accent} ${volume * 100}%, rgba(244,239,230,0.12) ${volume * 100}%)`, "--thumb": accent }}
                aria-label="How loud Griot speaks"
              />
              <Volume2 size={15} style={{ color: accent }} />
            </div>
            {ios && (
            <div className="flex gap-2">
              {[["speaker", "out loud"], ["private", "to your ear"]].map(([k, label]) => (
                <button
                  key={k}
                  data-testid={`flow-output-${k}`}
                  onClick={() => onOutput?.(k)}
                  className="flex-1 rounded-full border px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] transition-colors"
                  style={output === k
                    ? { borderColor: `${accent}99`, background: `${accent}1f`, color: accent }
                    : { borderColor: "var(--border-linen)", color: "var(--text-faint)" }}
                >
                  {label}
                </button>
              ))}
            </div>
            )}
            <p className="font-mono text-[9px] leading-[1.6] tracking-[0.1em]" style={{ color: "var(--text-faint)" }}>
              {ios
                ? "out loud goes to the speaker, to your ear to the earpiece. your handset keys set the ceiling."
                : "while a page is listening, android keeps voices on the call channel — your keys move call volume, which never reaches zero. this slider does: all the way down is silence."}
            </p>
          </div>
        )}

        <FlowControls
          muted={muted}
          toggleMute={toggleMute}
          onSound={() => setSound((v) => !v)}
          soundOpen={sound}
          allowText={allowText}
          toText={toText}
          accent={accent}
        />

      </div>
    </div>
  );
};
